import React, { useMemo } from 'react';
import { ReactComponent as Arrow } from 'assets/icons/blue-arrow-left.svg';
import ReactPaginate from 'react-paginate';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import { twMerge } from 'tailwind-merge';

interface IPaginateBlockProps {
  itemsPerPage: number;
  totalItems: number;
  setNewOffset: (offset: number) => void;
  offsetParams?: string;
  customOffset?: number;
}

const PaginateBlock: React.FC<IPaginateBlockProps> = ({
  itemsPerPage,
  totalItems,
  setNewOffset,
  offsetParams = 'offset',
  customOffset = 0,
}) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const offset = customOffset || queryParams.get(offsetParams);

  const pageCount = useMemo(
    () => Math.ceil(totalItems / itemsPerPage),
    [totalItems, itemsPerPage],
  );

  const initialPage = useMemo(() => {
    return Math.ceil(Number(offset) / itemsPerPage);
  }, [itemsPerPage, offset]);

  const handlePageClick = (event: { selected: number }) => {
    const newOffset = (event.selected * itemsPerPage) % totalItems;
    const lastQuery: Record<string, string> = {};
    queryParams.forEach((value, key) => {
      lastQuery[key] = value;
    });
    navigate(
      {
        search: createSearchParams({
          ...lastQuery,
          [offsetParams]: newOffset.toString(),
        }).toString(),
      },
      { replace: true },
    );
    setNewOffset(newOffset);
  };

  return (
    <>
      <ReactPaginate
        breakLabel="..."
        nextLabel={<Arrow className="rotate-180 fill-med-gray-3 h-[12px]" />}
        onPageChange={handlePageClick}
        forcePage={initialPage}
        marginPagesDisplayed={isMobile ? 1 : 3}
        pageCount={pageCount}
        previousClassName={twMerge(
          'flex items-center justify-center',
          initialPage === 0
            ? 'opacity-50 pointer-events-none cursor-not-allowed'
            : 'hover:bg-hover-blue',
        )}
        nextClassName={twMerge(
          'flex items-center justify-center',
          initialPage >= pageCount - 1
            ? 'opacity-50 pointer-events-none cursor-not-allowed'
            : 'hover:bg-hover-blue',
        )}
        previousLabel={<Arrow className="fill-med-gray-3 h-[12px]" />}
        breakLinkClassName="px-3 py-1.5"
        nextLinkClassName="px-3 py-1.5"
        previousLinkClassName="px-3 py-1.5"
        renderOnZeroPageCount={null}
        disableInitialCallback
        className="flex border border-gray-opacity-15 rounded-md w-fit overflow-hidden text-sm"
        activeLinkClassName="!text-clc-blue"
        breakClassName="flex items-end justify-center underline text-med-gray-3 font-bold"
        pageLinkClassName="px-3 py-1.5 font-bold underline text-med-gray-3 flex items-center justify-center border border-transparent border-x-gray-opacity-15 hover:bg-hover-blue"
      />
    </>
  );
};

export default PaginateBlock;
